import { route, SandboxError, type Route } from '../adapter';
import { matches, newId, now, paginate, sortBy, type SandboxStore } from '../store';
import type { Shipment, ShipmentPatch } from '@/lib/events';

/** Shipments on the event and across events, with items linked to packing lines. */

const STATUS_FOR: Readonly<Record<string, string>> = { PRE_TRANSIT: 'pre-transit', TRANSIT: 'in-transit', DELIVERED: 'delivered', RETURNED: 'returned', FAILURE: 'failure' };

function allShipments(store: SandboxStore): Shipment[] {
  return [...store.shipments, ...store.otherShipments];
}

/** After a shipment's items change, every packing line says which shipments it travels in. */
function syncLinesFromShipment(store: SandboxStore, shipment: Shipment): void {
  const carried = new Map((shipment.items ?? []).filter((item) => item.packingLineId).map((item) => [item.packingLineId as string, item]));
  store.packingList = store.packingList.map((line) => {
    const others = (line.shipments ?? []).filter((entry) => entry.shipmentId !== shipment._id);
    const item = carried.get(line._id);
    const quantity = item ? Number(item.quantity) : undefined;
    return { ...line, shipments: item ? [...others, { shipmentId: shipment._id, ...(quantity && quantity !== line.quantity ? { quantity } : {}) }] : others };
  });
}

function applyPatch(store: SandboxStore, existing: Shipment | undefined, patch: ShipmentPatch): Shipment {
  const items = patch.items.map((item) => ({ name: item.name, quantity: item.quantity, ...(item.packingLineId ? { packingLineId: item.packingLineId } : {}) }));
  const shipment: Shipment = {
    ...(existing ?? { _id: newId(), relatedTo: store.eventId, createdAt: now(), trackingHistory: [] }),
    carrier: patch.carrier,
    trackingNumber: patch.trackingNumber,
    ...(patch.proNumber ? { proNumber: patch.proNumber } : {}),
    ...(patch.pieces ? { pieces: patch.pieces } : {}),
    ...(patch.pieceType ? { pieceType: patch.pieceType } : {}),
    ...(patch.status ? { status: patch.status } : existing?.status ? {} : { status: 'PRE_TRANSIT' }),
    vendor: patch.vendor,
    ...(patch.expectedArrivalDate ? { expectedArrivalDate: patch.expectedArrivalDate } : {}),
    viewableByClient: patch.viewableByClient,
    ...(patch.returnLabel !== undefined ? { returnLabel: patch.returnLabel } : {}),
    ...(patch.labelText !== undefined ? { labelText: patch.labelText || undefined } : {}),
    items,
  };
  return shipment;
}

export const shipmentRoutes: readonly Route[] = [
  route('GET', '/events/:id/shipments', ({ store, query }) => paginate(store.shipments, query, 100)),
  route('POST', '/events/:id/shipments', ({ store, body }) => {
    const shipment = applyPatch(store, undefined, body as ShipmentPatch);
    store.shipments = [...store.shipments, shipment];
    syncLinesFromShipment(store, shipment);
    return { data: shipment };
  }),
  route('GET', '/shipments', ({ store, query }) => {
    const status = query.get('status');
    let rows = allShipments(store).filter((row) => (status && status !== 'all' ? (STATUS_FOR[row.status ?? ''] ?? row.status?.toLowerCase()) === status : true));
    rows = rows.filter((row) => matches(query.get('search'), row.carrier, row.trackingNumber, row.vendor));
    const returnLabels = query.get('returnLabels');
    if (returnLabels === 'all') rows = rows.filter((row) => row.returnLabel === true);
    if (returnLabels === 'unused') rows = rows.filter((row) => row.returnLabelUnused === true);
    rows = sortBy(rows, query.get('sortField') ?? 'eta', query.get('sortDirection') ?? 'desc', (row, field) => (field === 'carrier' ? row.carrier : field === 'status' ? row.status : (row.eta ?? row.expectedArrivalDate)) ?? undefined);
    return paginate(rows, query);
  }),
  route('GET', '/shipments/:id', ({ store, params }) => {
    const shipment = allShipments(store).find((row) => row._id === params['id']);
    if (!shipment) throw new SandboxError(404, 'Shipment not found.');
    return { data: shipment };
  }),
  route('PUT', '/shipments/:id', ({ store, params, body }) => {
    const index = store.shipments.findIndex((row) => row._id === params['id']);
    const existing = store.shipments[index];
    if (!existing) throw new SandboxError(404, 'Shipment not found.');
    const shipment = applyPatch(store, existing, body as ShipmentPatch);
    store.shipments[index] = shipment;
    syncLinesFromShipment(store, shipment);
    return { data: shipment };
  }),
  route('DELETE', '/shipments/:id', ({ store, params }) => {
    store.shipments = store.shipments.filter((row) => row._id !== params['id']);
    store.packingList = store.packingList.map((line) => ({ ...line, shipments: (line.shipments ?? []).filter((entry) => entry.shipmentId !== params['id']) }));
    return { data: null };
  }),
  route('POST', '/shipments/:id/sync', () => ({ data: { synced: false, reason: 'Tracking is not fetched in the sandbox.' } })),
  route('POST', '/events/:id/shipments/email/preview', ({ store }) => ({
    data: { subject: `Your materials are on the way: ${store.event.programName ?? 'your event'}`, html: '<p>Your materials have shipped. Tracking details are below.</p>', text: 'Your materials have shipped. Tracking details are below.' },
  })),
  route('POST', '/events/:id/shipments/email', ({ store }) => ({ data: { sentAt: now(), to: (store.event.contacts ?? []).map((contact) => contact.email).filter((email): email is string => Boolean(email)), subject: `Your materials are on the way: ${store.event.programName ?? 'your event'}` } })),
];
